import { Loader2, Sparkles, RefreshCw, Download, Mail, Send, CalendarClock } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ordinal } from './capabilityAnalyticsUtils';

export function NarrativeCard({
  trend, narrative, narrLoading, narrCached, canAdmin, schedule, exporting,
  showEmail, emailTo, emailNote, emailSending,
  onRegenerate, onDownload, onToggleEmail, onEmailToChange, onEmailNoteChange, onSendEmail, onOpenSchedule,
}) {
  return (
    <Card className="border border-cyan-200 bg-gradient-to-r from-cyan-50/70 to-blue-50/40 shadow-sm" data-testid="cap-trend-narrative">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="h-8 w-8 rounded-lg bg-cyan-100 text-cyan-700 flex items-center justify-center shrink-0">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-bold uppercase tracking-wide text-cyan-800">AI trend narrative</p>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-cyan-700 hover:bg-cyan-100/60 gap-1"
                  onClick={() => onRegenerate(true)} disabled={narrLoading} data-testid="cap-narrative-regenerate">
                  {narrLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  Regenerate
                </Button>
                {narrative && (
                  <>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-cyan-700 hover:bg-cyan-100/60 gap-1"
                      onClick={onDownload} disabled={exporting} data-testid="cap-narrative-download">
                      {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                      Download
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-cyan-700 hover:bg-cyan-100/60 gap-1"
                      onClick={onToggleEmail} data-testid="cap-narrative-email-toggle">
                      <Mail className="h-3 w-3" /> Email
                    </Button>
                    {canAdmin && (
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-cyan-700 hover:bg-cyan-100/60 gap-1"
                        onClick={onOpenSchedule} data-testid="cap-narrative-schedule-toggle">
                        <CalendarClock className="h-3 w-3" /> Schedule
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
            {schedule?.enabled && schedule?.recipients?.length > 0 && (
              <p className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1" data-testid="cap-schedule-status">
                <CalendarClock className="h-3 w-3" /> Emailed automatically on the {ordinal(schedule.day_of_month)} of each month
                to {schedule.recipients.length} recipient{schedule.recipients.length !== 1 ? 's' : ''}
                {schedule.last_run_at ? ` · last sent ${new Date(schedule.last_run_at).toLocaleDateString()}` : ''}
              </p>
            )}
            {narrLoading && !narrative ? (
              <p className="text-sm text-slate-400 mt-1 flex items-center gap-1.5" data-testid="cap-narrative-loading">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading the last {(trend?.periods || []).length} months…
              </p>
            ) : narrative ? (
              <>
                <p className="text-sm text-slate-700 leading-relaxed mt-1" data-testid="cap-narrative-text">{narrative}</p>
                <p className="text-[10px] text-slate-400 mt-1.5">GPT-4o · {narrCached ? 'cached' : 'freshly generated'}</p>
                {showEmail && (
                  <div className="rounded-lg border border-cyan-200 bg-white/70 p-3 mt-2 space-y-2" data-testid="cap-narrative-email-form">
                    <p className="text-xs font-semibold text-slate-700">Forward this capability update to an exec or HRBP</p>
                    <input type="email" value={emailTo} onChange={(e) => onEmailToChange(e.target.value)}
                      placeholder="leader@company.com"
                      className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                      data-testid="cap-narrative-email-input" />
                    <textarea value={emailNote} onChange={(e) => onEmailNoteChange(e.target.value)}
                      placeholder="Optional note…" rows={2}
                      className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                      data-testid="cap-narrative-email-note" />
                    <div className="flex justify-end">
                      <Button size="sm" className="h-8 text-xs gap-1.5 bg-cyan-600 hover:bg-cyan-700"
                        onClick={onSendEmail} disabled={emailSending} data-testid="cap-narrative-email-send">
                        {emailSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                        Send update
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-400 mt-1">No narrative available yet.</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
